/**
 * The console's shared pieces, all standing on the Mojo Up design language
 * (components/ui/Art.tsx and mojoup-art.css): a view header in the view's
 * tone, stats, empty states and sections. Status is always words or a shape
 * beside a colour; rows carry no repeated text badges.
 */

import { AlertDialog, Button, Checkbox, ListBox, Select, Switch } from '@heroui/react';
import { ActionBar } from '@heroui-pro/react';
import { createContext, type CSSProperties, type ReactNode, useContext, useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { ArtEmpty, ArtSection, ArtStat, type ArtTone, statNumber, ViewHeader } from './ui/Art.tsx';
import { ListBlock, ListGroup, ListRow, PageColumn, type PageWidth, SectionHeading } from './ui/Layout.tsx';

export { ArtEmpty, ArtSection, ArtStat, ListBlock, ListGroup, ListRow, PageColumn, SectionHeading, statNumber, ViewHeader };
export type { ArtTone, PageWidth };

export function PageHeader({ tone, icon, title, subtitle, actions, children }: { tone: ArtTone; icon: ReactNode; title: string; subtitle?: string; actions?: ReactNode; children?: ReactNode }) {
  return (
    <ViewHeader tone={tone} icon={icon} title={title} blurb={subtitle} actions={actions}>
      {children}
    </ViewHeader>
  );
}

export function Panel({ tone, icon, title, count, hint, actions, flush, children, className }: { tone: ArtTone; icon: ReactNode; title: ReactNode; count?: number; hint?: ReactNode; actions?: ReactNode; flush?: boolean; children: ReactNode; className?: string }) {
  return (
    <ArtSection tone={tone} icon={icon} title={title} count={count} hint={hint} actions={actions} flush={flush} className={className}>
      {children}
    </ArtSection>
  );
}

/** `selected`: a pressable stat used as a filter, shown as on. */
export function Stat({ tone, icon, label, value, detail, needsYou, onPress, selected }: { tone: ArtTone; icon: ReactNode; label: string; value: ReactNode | undefined; detail?: string; needsYou?: boolean; onPress?: () => void; selected?: boolean }) {
  return <ArtStat tone={tone} icon={icon} label={label} value={typeof value === 'number' ? statNumber(value) : (value ?? '—')} detail={detail ?? ''} needsYou={needsYou} onPress={onPress} {...(selected !== undefined ? { selected } : {})} />;
}

export function Empty({ tone, icon, title, body, action, compact }: { tone: ArtTone; icon: ReactNode; title: string; body?: string; action?: ReactNode; compact?: boolean }) {
  return (
    <ArtEmpty tone={tone} icon={icon} title={title} action={action} compact={compact}>
      {body}
    </ArtEmpty>
  );
}

/** A state as a dot plus its word, so colour is never the only signal. `wrap`: a sentence that may run onto a second line. */
export function StatusMark({ state, label, wrap }: { state: 'connected' | 'online' | 'offline' | 'working' | 'idle' | 'error' | 'low' | 'medium' | 'high' | 'disabled'; label?: string; wrap?: boolean }) {
  return (
    <span className={`inline-flex gap-1.5 text-sm ${wrap ? 'min-w-0 items-baseline [&>.status-dot]:shrink-0 [&>.status-dot]:translate-y-[-1px]' : 'items-center whitespace-nowrap'}`}>
      <span className="status-dot" data-state={state} aria-hidden="true" />
      {label ?? state[0]!.toUpperCase() + state.slice(1)}
    </span>
  );
}

export function ErrorNote({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <div role="alert" className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
      {error}
    </div>
  );
}

/** Inside a Sheet the sheet carries the title and the surface: a PlainPanel there shows only its body. */
export const BarePanels = createContext(false);

/**
 * A calm group for settings and forms: a small heading and a muted line outside, then one card whose
 * direct children are rows divided by hairlines (ListGroup in ui/Layout.tsx). Put ListRows straight
 * in; a single wrapper becomes one padded block. No artwork.
 */
export function PlainPanel({ id, title, description, actions, children }: { id?: string; title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const bare = useContext(BarePanels);
  if (bare) return <div className="grid gap-3">{children}</div>;
  return (
    <ListGroup id={id} title={title} description={description} actions={actions}>
      {children}
    </ListGroup>
  );
}

export const inputClass = 'w-full rounded-lg border border-field-border bg-field-background px-3 py-1.5 text-control outline-none focus:border-accent';
/** The same field, sized to its content: for a select beside a picker or another field. */
export const selectClass = 'rounded-lg border border-field-border bg-field-background px-3 py-1.5 text-control outline-none focus:border-accent';

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

/**
 * The page frame every view uses: header band, then stats, then sections, in a PageColumn
 * (ui/Layout.tsx): `narrow` is the 720px form column, otherwise the 1440px data column; both centred.
 */
export function Page({ children, narrow, width }: { children: ReactNode; narrow?: boolean; width?: PageWidth }) {
  return <PageColumn width={width ?? (narrow ? 'form' : 'data')}>{children}</PageColumn>;
}

/** One answer from a short list, as HeroUI's Select: for four or more answers (a Segment holds two or three). */
export function ChoiceSelect<T extends string>({ value, options, onChange, label, className, placeholder, isDisabled }: { value: T | undefined; options: Record<T, string>; onChange: (v: T) => void; label: string; className?: string; placeholder?: string; isDisabled?: boolean }) {
  return (
    <Select aria-label={label} isDisabled={isDisabled} className={className ?? 'w-fit min-w-44 max-w-full'} variant="secondary" placeholder={placeholder} value={value ?? null} onChange={(k) => k !== null && onChange(String(k) as T)}>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {(Object.keys(options) as T[]).map((k) => (
            <ListBox.Item key={k} id={k} textValue={options[k]}>
              {options[k]}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

/** HeroUI's checkbox with its label beside it. */
export function Tick({ checked, onChange, children, label, isDisabled }: { checked: boolean; onChange: (v: boolean) => void; children?: ReactNode; label?: string; isDisabled?: boolean }) {
  return (
    <Checkbox isSelected={checked} onChange={onChange} aria-label={label} isDisabled={isDisabled}>
      <Checkbox.Content className="font-normal">
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        {children}
      </Checkbox.Content>
    </Checkbox>
  );
}

/** HeroUI's switch at the workbench's size, for a setting that is on or off; it sits at the trailing end of its row. */
export function Toggle({ checked, onChange, label, isDisabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; isDisabled?: boolean }) {
  return (
    <Switch size="sm" isSelected={checked} onChange={onChange} aria-label={label} isDisabled={isDisabled}>
      {/* Switch.Content is the pressable part (the field alone is not, in this HeroUI). */}
      <Switch.Content>
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Content>
    </Switch>
  );
}

/**
 * A confirmation that says what follows, not "Are you sure?". `children` is the consequence;
 * `onConfirm` may be async, and the dialog stays open, pending, until it settles.
 */
export function ConfirmDialog({ isOpen, title, children, confirmLabel, danger, onConfirm, onCancel }: { isOpen: boolean; title: string; children: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => unknown; onCancel: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <AlertDialog.Backdrop isOpen={isOpen} onOpenChange={(open) => !open && !busy && onCancel()}>
      <AlertDialog.Container>
        <AlertDialog.Dialog className="sm:max-w-lg">
          <AlertDialog.Header>
            <AlertDialog.Icon status={danger ? 'danger' : 'accent'} />
            <AlertDialog.Heading>{title}</AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Body>
            <div className="grid gap-2 text-sm text-muted">{children}</div>
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button variant="tertiary" isDisabled={busy} onPress={onCancel}>
              Cancel
            </Button>
            <Button
              variant={danger ? 'danger' : 'primary'}
              isPending={busy}
              onPress={() => {
                setBusy(true);
                void Promise.resolve()
                  .then(onConfirm)
                  .finally(() => setBusy(false));
              }}
            >
              {confirmLabel}
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
}

const noSubscription = () => () => {};
const shellMain = () => document.querySelector<HTMLElement>('main.app-content');

/**
 * Where the view's content column is: the shell's <main> (`.app-content`) less its padding and its
 * scrollbar, as left and right insets from the window. It follows the sidebar as it opens and
 * collapses (the main resizes) and the window as it resizes. Undefined outside the shell.
 */
function useContentInsets(host: HTMLElement | null): { left: number; right: number } | undefined {
  const [insets, setInsets] = useState<{ left: number; right: number }>();
  useLayoutEffect(() => {
    if (!host) return;
    const measure = () => {
      const rect = host.getBoundingClientRect();
      const style = getComputedStyle(host);
      const start = rect.left + host.clientLeft + parseFloat(style.paddingLeft);
      const end = rect.left + host.clientLeft + host.clientWidth - parseFloat(style.paddingRight);
      const left = Math.round(start);
      const right = Math.round(document.documentElement.clientWidth - end);
      setInsets((was) => (was && was.left === left && was.right === right ? was : { left, right }));
    };
    // An observer reports the size it starts with, so the first measurement needs no call of its own.
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    window.addEventListener('resize', measure);
    // The sidebar animates its width; the main follows it frame by frame, and the observer with it.
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [host]);
  return insets;
}

/**
 * The floating bar a draft brings: what changed, then Discard and the one primary action. Shown only
 * while there is something to save. It floats over the foot of the window centred on the content
 * column (the area beside the sidebar, open or collapsed), and while it shows the page gains room at
 * its foot so the last row can be scrolled clear of it. HeroUI Pro's ActionBar is fixed to the window
 * (`.action-bar { position: fixed; inset-inline: 0 }`); its insets are set to the column's here.
 */
export function SaveBar({ isOpen, label, onDiscard, onSave, saveLabel, saving, isDisabled }: { isOpen: boolean; label: ReactNode; onDiscard: () => void; onSave: () => void; saveLabel: string; saving?: boolean; isDisabled?: boolean }) {
  // The shell's <main>, read at each render: it is there by the time a draft opens the bar.
  const host = useSyncExternalStore(noSubscription, shellMain, () => null);
  const insets = useContentInsets(host);
  const bar = (
    <div className="save-bar" style={insets ? ({ '--save-bar-left': `${insets.left}px`, '--save-bar-right': `${insets.right}px` } as CSSProperties) : undefined}>
      <ActionBar isOpen={isOpen} aria-label="Unsaved changes">
        <ActionBar.Prefix>
          <span className="px-2 text-sm tabular-nums">{label}</span>
        </ActionBar.Prefix>
        <ActionBar.Suffix>
          <Button size="sm" variant="ghost" onPress={onDiscard}>
            Discard
          </Button>
          <Button size="sm" variant="primary" isPending={saving} isDisabled={isDisabled} onPress={onSave}>
            {saveLabel}
          </Button>
        </ActionBar.Suffix>
      </ActionBar>
    </div>
  );
  if (!host) return bar;
  // The room at the page's foot is the main's last child, wherever in the view the bar was written.
  return createPortal(
    <>
      {isOpen ? <div className="save-bar-room" aria-hidden="true" /> : null}
      {bar}
    </>,
    host,
  );
}

export { Button };
